import { BusinessModel, LocationModel, ResellerModel, ReviewTargetModel, ReviewRequestCampaignModel, FeedbackModel, FilterFeedbackModel, ReminderEmailModel, SmsModel, ContactHistoryModel, ContactModel, WidgetModel, UserModel, GeneralRatingModel, ReportSettingsModel, DailyReportSettingsModel, FeedbackReportSettingsModel, GeneralReportSettingsModel, RatingPerLocationModel, StarsDistributionPerLocationModel, ReviewCountPerLocationModel, FeedbackCountPerLocationModel, SendEngineEfficiencyPerLocationModel, AvergeContactSpeedPerLocationModel, FeedbackReviewPercentagePerLocationModel, ReviewPerReviewSitePerLocationModel, SentimentCheckCountPerLocationModel, CampaignMetricsPerLocationModel, ContactQueuesModel, ClickedOnReviewPerLocationModel, GeneratedReportsModel, ScheduledReportScheduleModel, ScheduledReportModel, BasicReviewRequestCampaignInfo } from "@models/index"
import { CampaignState } from "../enums/campaingState.enum"
import { EditionState } from "../enums/editionCampaignState.enum"
import { Days } from "../enums/days.enum"
import { WorkflowModel } from '@models/workflow.model'
import { GeneralCampaignSettingsModel } from '@models/generalCampaingSettings.model'
import { ScheduleModel } from '@models/schedule.model'
import { SmsSettingsModel } from '@models/smsSettings.model'
import { EmailSettingsModel } from '@models/emailSettings.model'
import { EmailModel } from '@models/email.model'
import { LandingSettingsModel } from '@models/landingSettings.model'
import { TimeModel } from '@models/time.model'
import { TimeZoneModel } from '@models/timeZone.model'
import { SentimentCheck } from '@enums/sentimentChek.enum'
import { ReviewModel } from '@models/review.model'
import { ReviewResponseModel } from '@models/reviewResponse.model'
import { FilterReviewModel } from '@models/filterReview.model'

import { DateRangeModel } from '@models/dateRange.model'
import { DaysForReminder } from '@enums/daysForReminders.enum'
import { WidgetNameDisplay } from '@enums/widgetNameDisplay.enum'
import { WidgetDisplayMode } from '@enums/widgetDisplayMode.enum'
import { FeedbackReportTiming } from '@enums/feedbackReportTiming.enum'
import { GeneralReportTiming } from '@enums/GeneralReportTiming.enum'
import { RatingPerReviewSitePerLocationModel } from '@models/ratingPerReviewSitePerLocation.model'
import { days } from './const-utils'
import { ScheduledReportPeriodicity } from '@enums/scheduledReportPeriodicity.enum'
import { DataDeletionRequestModel } from "@models/dataDeletionRequestModel.model"
import { FacebookErrorModel } from "@models/facebookError.model"
import { Role } from "@enums/role.enum"

export const emptyBusinessModel = (): BusinessModel => {
    return {
        id: null,
        name: "",
        shortName: "",
        accountId: "",
        resellerId: "",
        pinned: false,
        logo: '',
        deleted: 0,
        countLocations: 0,
        mainAddress: '',
        countReviews: 0,
        countSentReviewRequests: 0,
        trialMode: false,
        trialStartDate: -1,
        trialEndDate: -1,
        phoneNumber: "",
        zipCode: ""
    }
}

export const emptyLocationModel = (): LocationModel => {
    return {
        name: '',
        phoneNumber: null,
        zipCode: null,
        maxRequestQuota: 200,
        address: null,
        parentBusiness: null,
        id: null,
        shortName: '',
        countryCode: '',
        isMultiCampaignEnabled: false,
        isSentimentCheckEnabled: false,
        isZapierEnabled: false,
        leaderBoardUrl: '',
        preferredCampaign: '',
    }
}

export const emptyContactModel = (): ContactModel => {
    return {
        id: null,
        name: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        addMethod: ''
    }
}

export const emptyUserModel = (): UserModel => {
    return {
        firstName: "",
        lastName: "",
        email: "",
        role: {
            id: '',
            name: Role.None,
            shortName: '',
            labelName: '',
            createdAt: null,
            updatedAt: null,
            deletedAt: null,
            deleted: false,
        },
        permissions: []
    }
}

export const emptyResellerModel = (): ResellerModel => {
    return {
        id: null,
        name: '',
        logo: '',
        siteURL: '',
        favIcon: './assets/images/star.ico',
        fromEmail: '',
        helpEmail: '',
        availableLicenses: 10,
        usedLicenses: 0,
        bgImage: '',
        enabledUpgradeModal: false
    }
}

export const emptyReviewTargetModel = (): ReviewTargetModel => {
    return {
        priority: null,
        name: null,
        prefix: '',
        id: null,
        logo: '',
        url: null,
        enable: false,
        custom: false,
        popular: false,
        checkUsOut: false,
        color: null,
        isReviewTarget: false
    }
}

export const emptyContactHistoryModel = (): ContactHistoryModel => {
    return {
        contactId: null,
        campaigns: []
    }
}

export const emptyWorkflowModel = (): WorkflowModel => {
    return {
        enableSentimentCheck: true,
        sentimentCheckType: SentimentCheck.Thumbs,
        sentimentCheckSettings: "",
        allowContacts: true
    }
}

export const emptyTimeModel = (): TimeModel => {
    return {
        hour: 0,
        minute: 0,
        second: 0
    }
}

export const emptyTimeZoneModel = (): TimeZoneModel => {
    return {
        name: '',
        abbr: ''
    }
}

export const emptyScheduleModel = (): ScheduleModel => {
    return {
        days: [Days.Monday, Days.Tuesday, Days.Wednesday, Days.Thursday, Days.Friday, Days.Saturday],
        startTime: { hour: 9, minute: 0, second: 0 },
        endTime: { hour: 17, minute: 0, second: 0 },
        timeZone: { abbr: '(CST)', name: 'America/Chicago' },
    }
}

export const emptyGeneralCampaignSettingsModel = (): GeneralCampaignSettingsModel => {
    return {
        name: 'My Campaign',
        senderName: '',
        quota: 1,
        methods: ['email', 'sms'],
        color: '#E4E4E4',
        stopCampaign: onClickToReviewFeedback,
        daysForDuplicates: 180,
        delayEmail: emptyTimeModel(),
        delaySMS: emptyTimeModel(),
        schedule: emptyScheduleModel(),
        enablePrivateFeedback: true
    }
}

export const emptyReviewRequestSmsModel = (): SmsModel => {
    return {
        label: 'First Contact Template',
        smsBody: '{contact_name}, thanks for choosing {location_name}. Would you please take 30 seconds to review your experience with us?',
        daysForReminder: 0,
        minDaysForReminder: 0
    }
}

export const emptyReminderSmsModel = (): SmsModel => {
    return {
        label: '',
        smsBody: '{contact_name}, just a friendly reminder to leave some quick feedback for {location_name} here ',
        daysForReminder: 7,
        minDaysForReminder: 7
    }
}

export const emptySmsSettingsModel = (): SmsSettingsModel => {
    return {
        reviewRequest: emptyReviewRequestSmsModel(),
        reminders: []
    }
}

export const emptyEmailReviewRequestModel = (): EmailModel => {
    return {
        label: 'First Contact Template',
        subject: 'Please share your feedback',
        header: 'Would you recommend us to your friends and family?',
        wysiwyg: '<p><span style="color: rgb(38, 50, 56);">Can you take 30 seconds to leave us a review? Your feedback helps us improve our business, and better serve you and other customers in the future!</span></p>',
        previewImage: '',
        daysForReminder: 0,
        minDaysForReminder: 0
    }
}
export const emptyEmailReminderModel = (): ReminderEmailModel => {
    return {
        label: '',
        subject: '{contact_name}, a quick reminder',
        header: 'Would you recommend us to your friends and family?',
        wysiwyg: '<p><span style="color: rgb(38, 50, 56);">Can you take 30 seconds to leave us a review? Your feedback helps us improve our business, and better serve you and other customers in the future!</span></p>',
        previewImage: '',
        daysForReminder: 7,
        minDaysForReminder: 7
    }
}

export const emptyEmailSettingsModel = (): EmailSettingsModel => {
    return {
        reviewRequest: emptyEmailReviewRequestModel(),
        reminders: []
    }
}

export const emptyLandingSettingsModel = (): LandingSettingsModel => {
    return {
        wyswygSentimentCheck: '<h1 class="ql-align-center"><strong>Would you recommend us to your </strong></h1><h1 class="ql-align-center"><strong>friends and family?</strong></h1><p class="ql-align-center"><br></p><p class="ql-align-center">Your feedback helps us improve our business, and better serve you and others in the future!</p><p class="ql-align-center"><br></p><p class="ql-align-center"><br></p><p class="ql-align-center"><strong style="background-color: rgb(187, 187, 187);"> Sentiment Check Buttons </strong></p>',
        wyswygReviewSite: '<h1 class="ql-align-center"><strong>Would you recommend us to your </strong></h1><h1 class="ql-align-center"><strong>friends and family?</strong></h1><p class="ql-align-center"><br></p><p class="ql-align-center">Can you take 30 seconds to leave us a review? Your feedback helps us improve</p><p class="ql-align-center">our business, and better serve you and other customers in the future!</p><p class="ql-align-center"><br></p><p class="ql-align-center">See what other customers say about us:</p><p class="ql-align-center"><br></p><p class="ql-align-center"><strong style="background-color: rgb(187, 187, 187);"> Review Site Buttons </strong></p><p class="ql-align-center"><br></p><p class="ql-align-center">See what other customers say about us:</p><p class="ql-align-center"><br></p><p class="ql-align-center"><strong style="background-color: rgb(187, 187, 187);"> Review Check us Out Sites </strong></p><p class="ql-align-center"><br></p><p class="ql-align-center">Your experience was not great?</p><p class="ql-align-center"><br></p><p class="ql-align-center"><strong style="background-color: rgb(187, 187, 187);"> Feedback Button </strong></p>',
        wyswygFeedbackMessage: '<h1 class="ql-align-center"><strong>We strive for 100% client satisfaction. If we fell short, </strong></h1><h1 class="ql-align-center"><strong><span class="ql-cursor">﻿</span>Please tell us more so we can address your concerns.</strong></h1>',
        previewImageSentimentCheck: '',
        previewImageFeedbackMessage: '',
        previewImageReviewSite: '',
        removePowered: false,
        thumbsUpText: 'Yes',
        thumbsDownText: 'Probably not',
        happyFaceText: 'Absolutely yes',
        neutralFaceText: 'Maybe',
        sadFaceText: 'Absolutely not',
        oneStarText: 'Absolutely not',
        twoStarsText: 'No',
        threeStarsText: 'Maybe',
        fourStarsText: 'Yes',
        fiveStarsText: 'Absolutely yes',
        url: 'https://reviews.localbusinessally.com/mylocation1234'
    }
}

export const emptyReviewRequestCampaignModel = (): ReviewRequestCampaignModel => {
    return {
        id: null,
        locationId: '',
        state: CampaignState.Waitting,
        editionState: EditionState.Draft,
        workflow: emptyWorkflowModel(),
        generalSettings: emptyGeneralCampaignSettingsModel(),
        smsSettings: emptySmsSettingsModel(),
        emailSettings: emptyEmailSettingsModel(),
        landingSettings: emptyLandingSettingsModel(),
        createDate: -1,
        updateDate: -1,
        isDisabledMultiCampaign: true
    }
}

export const emptyReviewResponseModel = (): ReviewResponseModel => {
    return {
        id: null,
        senderName: '',
        dateResponse: -1,
        messageResponse: '',
        lastUpdate: -1,
        photoUrl: ''
    }
}

/**
 * Returns an empty empty DataDeletionRequestModel for initializing purposes
 */
export const emptyDataDeletionRequestModel = (): DataDeletionRequestModel => {
    return {
        userId: '',
        userEmail: ''
    }
}

export const emptyReviewModel = (): ReviewModel => {
    return {
        reviewShakeId: null,
        id: null,
        locationId: null,
        reviewSite: '',
        datePosted: -1,
        rating: -1,
        message: '',
        userName: '',
        userPhotoUrl: '',
        title: '',
        reviewUrl: '',
        languageCode: '',
        gradeUsReviewId: null,
        googleReviewId: null,
        lastUpdate: -1,
        reviewResponses: [],
        reviewSiteIcon: '',
        reviewSiteName: ''
    }
}

export const emptyDateRangeModel = (): DateRangeModel => {
    return {
        startDate: null,
        endDate: null
    }
}

export const emptyFilterReviewModel = (): FilterReviewModel => {
    return {
        filterByText: null,
        filterByDate: emptyDateRangeModel(),
        filterByRating: null,
        filterByReviewSites: null,
        filterByMessage: null
    }
}


export const emptyFeedbackModel = (): FeedbackModel => {
    return {
        id: null,
        locationId: null,
        profileId: null,
        userPhotoUrl: '',
        datetime: -1,
        name: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        ipAddress: '',
        actionTaken: '',
    }
}

export const emptyFilterFeedbackModel = (): FilterFeedbackModel => {
    return {
        filterByText: null,
        filterByDate: emptyDateRangeModel()
    }
}

/**
 * emptyWidgetModel returns the
 * default {@link WidgetModel}
 * used across the app to avoid
 * incongruous states
 */
export const emptyWidgetModel = (): WidgetModel => {
    return {
        filterByRating: [4, 5],
        filterByReviewSites: null,
        onlyReviewsWithText: false,
        nameDisplay: WidgetNameDisplay.Long,
        bgColor: '#ffffff',
        numberOfReviews: 30,
        reviewsPerPage: 10,
        displayMode: WidgetDisplayMode.Stack,
        showOverallRating: false,
        showLeaveReviewButton: false
    }
}

export const phoneNumberMask = {
    guide: true,
    showMask: false,
    mask: ['(', /\d/, /\d/, /\d/, ')', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
};

export const USPhoneMask = {
    guide: true,
    showMask: false,
    mask: [/\D/, /\d/, ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
};

export const CAPhoneMask = {
    guide: true,
    showMask: false,
    mask: [/\D/, /\d/, ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
};

export const UKPhoneMask = {
    guide: true,
    showMask: false,
    mask: [/\D/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/]
};

export const AUPhoneMask = {
    guide: true,
    showMask: false,
    mask: [/\D/, /\d/, /\d/, ' ', /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/]
};

export const zipCodeMask = {
    guide: false,
    showMask: false,
    mask: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
};
export const emptyGeneralRatingModel = (): GeneralRatingModel => {
    return {
        readOnly: "true",
        rating: "0.0",
        ratingColor: "",
        fillColor: "#ffb200",
        graphLinesColor: "#ffb200",
        progressBarLabelsColor: "",
        progressBarPercentageValues: [0, 0, 0, 0, 0],
        countingPerStars: [0, 0, 0, 0, 0]
    }
}
export const emptyReportsSettingsModel = (): ReportSettingsModel => {
    return {
        businessId: '',
        dailyReport: emptyDailyReportSettingsModel(),
        feedbackReport: emptyFeedbackReportsSettingsModel(),
        generalReport: emptyGeneralReportsSettingsModel(),
        updatedReport: ''
    }
}
export const emptyGeneratedReportsModel = (): GeneratedReportsModel => {
    return {
        id: '',
        businessId: '',
        name: '',
        period: emptyDateRangeModel(),
        createdDate: -1,
        lastUpdated: -1,
        url: '',
        key: '',
    }
}
export const emptyDailyReportSettingsModel = (): DailyReportSettingsModel => {
    return {
        locations: [],
        users: []
    }
}
export const emptyFeedbackReportsSettingsModel = (): FeedbackReportSettingsModel => {
    return {
        locations: [],
        users: [],
        reportTiming: FeedbackReportTiming.Immediately
    }
}
export const emptyGeneralReportsSettingsModel = (): GeneralReportSettingsModel => {
    return {
        locations: [],
        users: [],
        reportTiming: GeneralReportTiming.Monthly
    }
}
export const emptySentimentCheckCountPerLocationModel = (): SentimentCheckCountPerLocationModel => {
    return {
        locationId: '',
        sentimentCheckCountResponse: {
            countStars: [0, 0, 0, 0, 0],
            countThumbs: [0, 0],
            countFaces: [0, 0, 0]
        }
    }
}
export const emptyCampaignMetricsPerLocationModel = (): CampaignMetricsPerLocationModel => {
    return {
        locationId: '',
        data: {
            email: [0, 0, 0, 0, 0],
            sms: [0, 0, 0, 0, 0]
        }
    }
}
export const emptyClickedOnReviewPerLocationModel = (): ClickedOnReviewPerLocationModel => {
    return {
        locationId: '',
        clickedReviews: 0
    }
}
export const emptyContactsQueueModel = (): ContactQueuesModel => {
    return {
        queue: [],
        sent: []
    }
}

export const emptyScheduledReportScheduleModel = (): ScheduledReportScheduleModel => {
    return {
        periodicity: ScheduledReportPeriodicity.Weekly,
        day: days[0],
        timeFrame: scheduledReportWeeklyTimeFrames[3].value
    }
}

export const emptyScheduledReportModel = (): ScheduledReportModel => {
    return {
        name: 'New Report',
        locations: [],
        users: [],
        schedule: emptyScheduledReportScheduleModel()
    }
}


export const weekDays: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
export const daysForRemindersOpt: number[] = [
    DaysForReminder.Opt1,
    DaysForReminder.Opt2,
    DaysForReminder.Opt3,
    DaysForReminder.Opt4,
    DaysForReminder.Opt5,
    DaysForReminder.Opt6,
    DaysForReminder.Opt7,
    DaysForReminder.Opt8,
    DaysForReminder.Opt9,
    DaysForReminder.Opt10,
]

export const feedbackReportTiming: { label: string, value: string }[] = [
    { label: 'Immediately', value: 'immediately' },
    { label: 'Every hour', value: '1-hour' },
    { label: 'Every 2 hours', value: '2-hours' },
    { label: 'Every 3 hours', value: '3-hours' },
    { label: 'Every 4 hours', value: '4-hours' },
    { label: 'Every 5 hours', value: '5-hours' },
    { label: 'Once per day', value: 'once' },
    { label: 'Twice per day', value: 'twice' },
    { label: 'Three times per day', value: 'thrice' }
];

export const generalReportTiming: { label: string, value: string }[] = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Every two weeks', value: 'two-weeks' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Every two months', value: 'two-months' },
    { label: 'Every three months', value: 'three-months' },
    { label: 'Every four months', value: 'four-months' },
    { label: 'Every five months', value: 'five-months' },
    { label: 'Every six months', value: 'six-months' }
]

export const scheduledReportWeeklyTimeFrames: { label: string, value: number }[] = [
    { label: 'One week ago', value: 604800000 },
    { label: 'Two weeks ago', value: 1209600000 },
    { label: 'Three Weeks ago', value: 1814400000 },
    { label: 'Four weeks ago', value: 2419200000 }
]

export const scheduledReportMonthlyTimeFrames: { label: string, value: number }[] = [
    { label: 'One month ago', value: 2592000000 },
    { label: 'Two months ago', value: 5184000000 },
    { label: 'Three months ago', value: 7776000000 },
    { label: 'Four months ago', value: 10368000000 },
    { label: 'Five months ago', value: 12960000000 },
    { label: 'Six months ago', value: 15552000000 },
    { label: 'Twelve months ago', value: 31104000000 },
]


export const onClickToReviewFeedback = false;
export const onOpenEmailSMS = true;
export const intercomAppId = "uqwsn9ce";
export const zillowUrlPrefix = "https://www.zillow.com/reviews/write/?s=";


/**
 * Returns an empty empty FacebookErrorModel for initializing purposes
 */
export const emptyFacebookErrorModel = (): FacebookErrorModel => {
    return {
        status: 0,
        error: {
            code: '',
            error_user_title: '',
            error_user_msg: '',
            loggerCode: ''
        },
        showable: false
    }
}

export const emptyBasicReviewRequestCampaignInfoModel = (): BasicReviewRequestCampaignInfo => {
    return {
        id: null,
        genericLandingURL: "",
        color: "#E4E4E4",
        name: "",
        isDisabledMultiCampaign: true
    }
}
